import React, { useEffect, useMemo, useState } from "react";
import api from "../services/api";
import { KILL_CHAIN, activeTab, useConsole } from "../lib/console";
import { usePoll } from "../lib/hooks";
import { ATTACK_LABELS, SEVERITIES, attackLabel, attackVar, duration, fmtDateTime, isIPv4, num, plural, sevLabel, sevTone, sevVar, toDate } from "../lib/format";
import {
  Badge,
  Button,
  Callout,
  Card,
  CountryTag,
  Drawer,
  Empty,
  ErrorState,
  Field,
  KV,
  Modal,
  SevBadge,
  SkeletonRows,
  cx,
  useToast,
} from "../components/ui";
import { Icon } from "../components/Icon";
import { PageHeader, PageTabs } from "../components/ConsoleShell";

const STATUS = {
  new: { label: "New", tone: "brand" },
  investigating: { label: "Investigating", tone: "medium" },
  resolved: { label: "Resolved", tone: "ok" },
  false_positive: { label: "False positive", tone: "neutral" },
  info: { label: "Informational", tone: "neutral" },
};
const statusOf = (s) => STATUS[s] || { label: s || "New", tone: "neutral" };

// ── Campaigns ───────────────────────────────────────────────────
function ChainStrip({ stages, crit }) {
  const hit = new Set(stages || []);
  return (
    <div className="chain" aria-label={`Kill-chain stages reached: ${(stages || []).join(", ") || "none"}`}>
      {KILL_CHAIN.map((s, i) => (
        <div key={s} className={cx("chain-step", hit.has(s) && "is-hit", hit.has(s) && crit && "is-crit")}>
          <div className="chain-step-n">{String(i + 1).padStart(2, "0")}</div>
          <div className="chain-step-name">{s}</div>
        </div>
      ))}
    </div>
  );
}

function Campaigns({ data, error, reload }) {
  const c = useConsole();
  const [all, setAll] = useState(false);
  if (error) return <ErrorState detail={error} onRetry={reload} />;
  if (!data) return <Card><SkeletonRows rows={6} /></Card>;
  const list = data.campaigns || [];
  if (!list.length)
    return (
      <Card>
        <Empty icon="route" title="No campaigns in the last 24 hours">
          A campaign is a source that moved through two or more kill-chain stages, or produced five or more detections.
        </Empty>
      </Card>
    );
  return (
    <div className="stack">
      <p className="small muted">
        {plural(data.total_campaigns ?? list.length, "campaign")} in the last 24 hours, ordered by kill-chain depth then volume. Stages are derived from the attack types each source used.
      </p>
      {list.slice(0, all ? list.length : 6).map((cp) => {
        const span = (toDate(cp.last_seen) - toDate(cp.first_seen)) / 1000;
        return (
          <Card
            key={cp.ip}
            className="is-accent"
            style={{ "--tone": sevVar(cp.max_severity) }}
            title={
              <span className="row row-wrap" style={{ gap: 10 }}>
                <span className="mono">{cp.ip}</span>
                <CountryTag code={cp.country_code} name={cp.country} />
              </span>
            }
            subtitle={`${plural(cp.total_events, "detection")} · ${plural(cp.stages.length, "stage")} · active for ${duration(span)} · last seen ${fmtDateTime(cp.last_seen)} GST`}
            actions={
              <>
                <SevBadge sev={cp.max_severity} />
                {cp.is_blocked ? (
                  <Badge tone="ok" dot>
                    Blocked
                  </Badge>
                ) : (
                  <Badge tone="medium">Active</Badge>
                )}
              </>
            }
            footer={
              <>
                <div className="row row-wrap" style={{ gap: 6 }}>
                  {cp.attack_types.map((t) => (
                    <span key={t} className="badge tone-neutral">
                      <i className="badge-dot" style={{ color: attackVar(t), background: attackVar(t) }} />
                      {attackLabel(t)}
                    </span>
                  ))}
                </div>
                <div className="row">
                  <Button size="sm" variant="ghost" icon="crosshair" onClick={() => c.hunt({ source_ip: cp.ip })}>
                    Hunt
                  </Button>
                  <Button size="sm" icon="user" onClick={() => c.openProfile(cp.ip)}>
                    Profile
                  </Button>
                </div>
              </>
            }
          >
            <ChainStrip stages={cp.stages} crit={cp.max_severity === "CRITICAL"} />
          </Card>
        );
      })}
      {list.length > 6 && (
        <div style={{ textAlign: "center" }}>
          <Button variant="ghost" onClick={() => setAll((v) => !v)}>
            {all ? "Show fewer" : `Show all ${list.length}`}
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Event case drawer (DB records from hunt / anomalies) ────────
function CaseDrawer({ event, onClose, onStatus }) {
  const c = useConsole();
  const toast = useToast();
  const [notes, setNotes] = useState(null);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(event?.status);

  useEffect(() => {
    if (!event) return;
    setStatus(event.status);
    setNotes(null);
    api
      .get(`/api/threats/${event.id}/notes`)
      .then((r) => setNotes(r.data.items || []))
      .catch(() => setNotes([]));
  }, [event]);

  if (!event) return null;

  const changeStatus = async (s) => {
    const prev = status;
    setStatus(s);
    try {
      await api.patch(`/api/threats/${event.id}/status`, { status: s });
      onStatus?.(event.id, s);
      toast(`Marked as ${statusOf(s).label.toLowerCase()}`, "ok");
    } catch (e) {
      setStatus(prev);
      toast("Couldn't update status", "critical", { detail: e?.response?.data?.detail || e.message });
    }
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSaving(true);
    try {
      await api.post(`/api/threats/${event.id}/notes`, { content: text.trim(), author: "analyst" });
      setText("");
      const r = await api.get(`/api/threats/${event.id}/notes`);
      setNotes(r.data.items || []);
    } catch (err) {
      toast("Couldn't save the note", "critical", { detail: err?.response?.data?.detail || err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open
      onClose={onClose}
      width={580}
      title={attackLabel(event.attack_type)}
      subtitle={`Case #${event.id} · ${fmtDateTime(event.timestamp)} GST`}
      icon={
        <span className={cx("sev-glyph", `tone-${sevTone(event.severity)}`)}>
          <Icon name="fileText" size={17} />
        </span>
      }
      footer={
        <>
          <a className="btn btn-ghost btn-md" href={`/api/threats/${event.id}/report`} target="_blank" rel="noreferrer">
            <Icon name="download" size={16} />
            <span>PDF report</span>
          </a>
          <Button variant="primary" icon="user" onClick={() => c.openProfile(event.source_ip)}>
            Source profile
          </Button>
        </>
      }
    >
      <div className="drawer-section">
        <h3>Case status</h3>
        <div className="seg" role="radiogroup" aria-label="Case status" style={{ flexWrap: "wrap" }}>
          {["new", "investigating", "resolved", "false_positive"].map((s) => (
            <button key={s} type="button" role="radio" aria-checked={status === s} className={cx("seg-item", status === s && "is-active")} onClick={() => changeStatus(s)}>
              {statusOf(s).label}
            </button>
          ))}
        </div>
      </div>
      <div className="drawer-section">
        <h3>Event</h3>
        <KV
          items={[
            ["Severity", <SevBadge sev={event.severity} />],
            ["Source", `${event.source_ip}${event.source_port ? `:${event.source_port}` : ""}`, { mono: true }],
            ["Target", `${event.destination_ip || "—"}${event.destination_port ? `:${event.destination_port}` : ""}`, { mono: true }],
            ["Location", <CountryTag code={event.country_code} name={event.country} />],
            ["Technique", event.mitre_technique_id ? `${event.mitre_technique_id} · ${event.mitre_technique}` : "—", { wrap: true }],
            ["Tactic", event.mitre_tactic],
            ["Confidence", event.confidence != null ? `${(event.confidence * 100).toFixed(1)}%` : "—"],
            ["Anomaly score", event.anomaly_score != null ? Number(event.anomaly_score).toFixed(3) : "—"],
          ]}
        />
      </div>
      <div className="drawer-section">
        <h3>Analyst notes</h3>
        <form onSubmit={addNote} className="col" style={{ gap: 8 }}>
          <textarea className="textarea" rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="Add what you found, decided or escalated…" aria-label="New note" />
          <div className="row" style={{ justifyContent: "flex-end" }}>
            <Button type="submit" size="sm" variant="primary" icon="plus" loading={saving} disabled={!text.trim()}>
              Add note
            </Button>
          </div>
        </form>
        <div className="timeline" style={{ marginTop: 16 }}>
          {notes == null && <SkeletonRows rows={2} />}
          {notes?.length === 0 && <span className="small muted">No notes yet.</span>}
          {notes?.map((n) => (
            <div key={n.id} className="timeline-item" style={{ "--tone": "var(--brand)" }}>
              <div className="small muted">
                {n.author} · {fmtDateTime(n.timestamp)}
              </div>
              <div style={{ whiteSpace: "pre-wrap" }}>{n.content}</div>
            </div>
          ))}
        </div>
      </div>
    </Drawer>
  );
}

function EventTable({ items, onOpen, showStatus = true, extra }) {
  return (
    <div className="table-wrap table-scroll" style={{ maxHeight: 620 }}>
      <table className="table">
        <thead>
          <tr>
            <th>Time (GST)</th>
            <th>Severity</th>
            <th>Detection</th>
            <th>Source</th>
            <th className="table-hide-sm">Technique</th>
            {extra && <th className="num">{extra.label}</th>}
            {showStatus && <th className="table-hide-sm">Case</th>}
          </tr>
        </thead>
        <tbody>
          {items.map((e) => (
            <tr
              key={e.id}
              className="is-clickable"
              tabIndex={0}
              onClick={() => onOpen(e)}
              onKeyDown={(ev) => {
                if (ev.key === "Enter") onOpen(e);
              }}
            >
              <td className="mono nowrap" style={{ fontSize: 12.5 }}>
                {fmtDateTime(e.timestamp)}
              </td>
              <td>
                <SevBadge sev={e.severity} />
              </td>
              <td>
                <span className="row" style={{ gap: 8 }}>
                  {attackLabel(e.attack_type)}
                </span>
              </td>
              <td>
                <div className="mono" style={{ fontSize: 12.5 }}>
                  {e.source_ip}
                </div>
                <CountryTag code={e.country_code} />
              </td>
              <td className="table-hide-sm mono muted" style={{ fontSize: 12 }}>
                {e.mitre_technique_id || "—"}
              </td>
              {extra && <td className="num mono">{extra.value(e)}</td>}
              {showStatus && (
                <td className="table-hide-sm">
                  <Badge tone={statusOf(e.status).tone}>{statusOf(e.status).label}</Badge>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Threat hunt ─────────────────────────────────────────────────
const EMPTY_HUNT = { source_ip: "", attack_type: "", severity: "", country: "", mitre_id: "", hours: "24", is_anomaly: "" };
const WINDOWS = [
  ["1", "Last hour"],
  ["6", "Last 6 hours"],
  ["24", "Last 24 hours"],
  ["72", "Last 3 days"],
  ["168", "Last 7 days"],
  ["720", "Last 30 days"],
];

function Hunt({ onOpen, results, setResults }) {
  const c = useConsole();
  const toast = useToast();
  const [form, setForm] = useState(EMPTY_HUNT);
  const [state, setState] = useState({ loading: false, error: null });
  const saved = usePoll("/api/saved-queries");
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [del, setDel] = useState(null);

  const run = async (f = form) => {
    setState({ loading: true, error: null });
    const params = { hours: Number(f.hours) || 24, limit: 200 };
    ["source_ip", "attack_type", "severity", "country", "mitre_id"].forEach((k) => {
      if (f[k]?.trim()) params[k] = f[k].trim();
    });
    if (f.is_anomaly) params.is_anomaly = f.is_anomaly === "true";
    try {
      const r = await api.get("/api/hunt", { params });
      setResults({ ...r.data, params });
      setState({ loading: false, error: null });
    } catch (e) {
      setState({ loading: false, error: e?.response?.data?.detail || e.message });
    }
  };

  // Prefill from elsewhere in the console (attack mix, profile, palette)
  useEffect(() => {
    if (!c.huntPrefill) return;
    const { _at, ...p } = c.huntPrefill;
    const f = { ...EMPTY_HUNT, ...Object.fromEntries(Object.entries(p).map(([k, v]) => [k, String(v)])) };
    setForm(f);
    run(f);
    c.clearHuntPrefill();
    // eslint-disable-next-line
  }, [c.huntPrefill]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const active = Object.entries(form).filter(([k, v]) => v && !(k === "hours" && v === "24")).length;

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/api/saved-queries", { name: saveName.trim(), query_params: form });
      setSaveOpen(false);
      setSaveName("");
      saved.reload();
      toast("Hunt saved", "ok");
    } catch (err) {
      toast("Couldn't save the hunt", "critical", { detail: err?.response?.data?.detail || err.message });
    }
  };
  const remove = async () => {
    try {
      await api.delete(`/api/saved-queries/${del.id}`);
      saved.reload();
      toast("Saved hunt removed", "ok");
    } catch {
      toast("Couldn't remove the saved hunt", "critical");
    }
    setDel(null);
  };

  const ipInvalid = form.source_ip && !/^[0-9a-fA-F.:]+$/.test(form.source_ip.trim());

  return (
    <div className="grid cols-12">
      <div className="span-8 stack">
        <Card title="Hunt across stored detections" subtitle="Every field is optional; combine them to narrow down" icon="crosshair">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              run();
            }}
          >
            <div className="form-grid">
              <Field label="Source IP (contains)" hint={ipInvalid ? "Use digits, dots or colons" : undefined}>
                <input className={cx("input input-mono", ipInvalid && "is-invalid")} value={form.source_ip} onChange={set("source_ip")} placeholder="e.g. 185.220." spellCheck={false} />
              </Field>
              <Field label="Attack type">
                <select className="select" value={form.attack_type} onChange={set("attack_type")}>
                  <option value="">Any</option>
                  {Object.entries(ATTACK_LABELS)
                    .filter(([k]) => k !== "normal")
                    .map(([k, l]) => (
                      <option key={k} value={k}>
                        {l}
                      </option>
                    ))}
                </select>
              </Field>
              <Field label="Severity">
                <select className="select" value={form.severity} onChange={set("severity")}>
                  <option value="">Any</option>
                  {SEVERITIES.map((s) => (
                    <option key={s} value={s}>
                      {sevLabel(s)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Country">
                <input className="input" value={form.country} onChange={set("country")} placeholder="e.g. Russia" />
              </Field>
              <Field label="MITRE technique">
                <input className="input input-mono" value={form.mitre_id} onChange={set("mitre_id")} placeholder="e.g. T1110" spellCheck={false} />
              </Field>
              <Field label="Time window">
                <select className="select" value={form.hours} onChange={set("hours")}>
                  {WINDOWS.map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Behaviour">
                <select className="select" value={form.is_anomaly} onChange={set("is_anomaly")}>
                  <option value="">All events</option>
                  <option value="true">Anomalies only</option>
                  <option value="false">Signature matches only</option>
                </select>
              </Field>
            </div>
            <div className="form-actions" style={{ justifyContent: "space-between" }}>
              <Button variant="ghost" icon="x" disabled={!active} onClick={() => setForm(EMPTY_HUNT)}>
                Reset
              </Button>
              <div className="row">
                <Button icon="plus" disabled={!active} onClick={() => setSaveOpen(true)}>
                  Save hunt
                </Button>
                <Button type="submit" variant="primary" icon="search" loading={state.loading} disabled={ipInvalid}>
                  Run hunt
                </Button>
              </div>
            </div>
          </form>
        </Card>

        <Card
          flush
          title="Results"
          subtitle={results ? `${num(results.total)} matching ${results.total === 1 ? "event" : "events"}${results.total > results.items.length ? ` · showing the newest ${results.items.length}` : ""}` : "Run a hunt to see matching events"}
          icon="list"
        >
          {state.error ? (
            <ErrorState detail={state.error} onRetry={() => run()} />
          ) : state.loading && !results ? (
            <div className="card-body">
              <SkeletonRows rows={6} />
            </div>
          ) : !results ? (
            <Empty icon="crosshair" title="Nothing hunted yet">
              Start broad, for example every critical event in the last 24 hours, then narrow by source or technique.
            </Empty>
          ) : results.items.length === 0 ? (
            <Empty icon="search" title="No events match">
              Widen the time window or remove a filter.
            </Empty>
          ) : (
            <EventTable items={results.items} onOpen={onOpen} />
          )}
        </Card>
      </div>

      <Card className="span-4" title="Saved hunts" subtitle="Reusable queries stored on the server" icon="layers" flush style={{ alignSelf: "start" }}>
        {saved.error ? (
          <ErrorState detail={saved.error} onRetry={saved.reload} />
        ) : !saved.data ? (
          <div className="card-body">
            <SkeletonRows rows={3} />
          </div>
        ) : !saved.data.items?.length ? (
          <Empty compact icon="layers">
            Save a hunt you run often and it will appear here.
          </Empty>
        ) : (
          <div className="list">
            {saved.data.items.map((q) => (
              <div key={q.id} className="list-item" style={{ gridTemplateColumns: "minmax(0,1fr) auto" }}>
                <span style={{ minWidth: 0 }}>
                  <span className="list-title" style={{ display: "block" }}>
                    {q.name}
                  </span>
                  <span className="list-meta">
                    {Object.entries(q.query_params || {})
                      .filter(([, v]) => v)
                      .map(([k, v]) => `${k.replace(/_/g, " ")}: ${k === "attack_type" ? attackLabel(v) : v}`)
                      .join(" · ") || "No filters"}
                  </span>
                </span>
                <span className="row" style={{ gap: 2 }}>
                  <Button
                    size="sm"
                    variant="ghost"
                    icon="play"
                    onClick={() => {
                      const f = { ...EMPTY_HUNT, ...q.query_params };
                      setForm(f);
                      run(f);
                    }}
                  >
                    Run
                  </Button>
                  <button type="button" className="icon-btn icon-btn-sm" aria-label={`Delete saved hunt ${q.name}`} title="Delete" onClick={() => setDel(q)}>
                    <Icon name="trash" size={14} />
                  </button>
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Modal open={saveOpen} onClose={() => setSaveOpen(false)} label="Save hunt">
        <form onSubmit={save}>
          <div className="modal-head">
            <h2>Save this hunt</h2>
            <p>Stores the current filters so anyone on the console can run them again.</p>
          </div>
          <div className="modal-body">
            <Field label="Name">
              <input className="input" data-autofocus value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="e.g. Critical brute force, 24h" maxLength={80} />
            </Field>
          </div>
          <div className="modal-foot">
            <Button variant="ghost" onClick={() => setSaveOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={!saveName.trim()}>
              Save
            </Button>
          </div>
        </form>
      </Modal>
      <Modal open={!!del} onClose={() => setDel(null)} label="Delete saved hunt">
        <div className="modal-head">
          <h2>Delete “{del?.name}”?</h2>
          <p>The saved query is removed for everyone. Events are not affected.</p>
        </div>
        <div className="modal-foot" style={{ marginTop: 16 }}>
          <Button variant="ghost" onClick={() => setDel(null)}>
            Cancel
          </Button>
          <Button variant="danger" icon="trash" onClick={remove} data-autofocus>
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
}

// ── Anomalies ───────────────────────────────────────────────────
function Anomalies({ data, error, reload, onOpen }) {
  if (error) return <ErrorState detail={error} onRetry={reload} />;
  const list = data?.anomalies;
  return (
    <div className="stack">
      <Callout tone="brand" icon="sparkle">
        Behavioural anomalies are flagged by the unsupervised models (Isolation Forest and autoencoder) because they deviate from normal traffic, not because they match a known signature.
        On their own they never trigger an automatic block.
      </Callout>
      <Card flush title="Anomalous events" subtitle="Last 24 hours, newest first" icon="sparkle">
        {!list ? (
          <div className="card-body">
            <SkeletonRows rows={6} />
          </div>
        ) : !list.length ? (
          <Empty icon="checkCircle" tone="ok" title="No anomalies in the last 24 hours" />
        ) : (
          <EventTable items={list} onOpen={onOpen} extra={{ label: "Anomaly", value: (e) => (e.anomaly_score != null ? Number(e.anomaly_score).toFixed(2) : "—") }} />
        )}
      </Card>
    </div>
  );
}

// ── Indicators (IOCs) ───────────────────────────────────────────
const IOC_TYPES = [
  ["ip", "IP address"],
  ["domain", "Domain"],
  ["url", "URL"],
  ["hash", "File hash"],
  ["email", "Email"],
];

function Indicators() {
  const toast = useToast();
  const iocs = usePoll("/api/iocs");
  const [form, setForm] = useState({ ioc_type: "ip", value: "", severity: "HIGH", description: "" });
  const [adding, setAdding] = useState(false);
  const [check, setCheck] = useState("");
  const [result, setResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [del, setDel] = useState(null);

  const add = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await api.post("/api/iocs", { ...form, value: form.value.trim(), description: form.description.trim() || null });
      toast("Indicator added", "ok");
      setForm((f) => ({ ...f, value: "", description: "" }));
      iocs.reload();
    } catch (err) {
      toast(err?.response?.status === 409 ? "That indicator already exists" : "Couldn't add the indicator", "critical");
    } finally {
      setAdding(false);
    }
  };

  const runCheck = async (e) => {
    e.preventDefault();
    const v = check.trim();
    if (!v) return;
    setChecking(true);
    const [local, feed] = await Promise.allSettled([api.post("/api/iocs/search", { value: v }), api.get(`/api/feeds/lookup/${encodeURIComponent(v)}`)]);
    setResult({
      value: v,
      local: local.status === "fulfilled" ? local.value.data : null,
      feed: feed.status === "fulfilled" ? feed.value.data : null,
    });
    setChecking(false);
    iocs.reload();
  };

  const remove = async () => {
    try {
      await api.delete(`/api/iocs/${del.id}`);
      toast("Indicator deactivated", "ok");
      iocs.reload();
    } catch {
      toast("Couldn't deactivate the indicator", "critical");
    }
    setDel(null);
  };

  const items = iocs.data?.items;

  return (
    <div className="grid cols-12">
      <div className="span-4 stack">
        <Card title="Check a value" subtitle="Against your indicators and public feeds" icon="search">
          <form onSubmit={runCheck} className="col" style={{ gap: 10 }}>
            <input className="input input-mono" value={check} onChange={(e) => setCheck(e.target.value)} placeholder="IP, domain, URL or hash" aria-label="Value to check" spellCheck={false} />
            <Button type="submit" variant="primary" icon="search" loading={checking} disabled={!check.trim()}>
              Check
            </Button>
          </form>
          {result && (
            <div className="col" style={{ gap: 8, marginTop: 14 }}>
              {result.local?.matched ? (
                <Callout tone="critical" icon="alert" title="Matches your indicators">
                  {result.local.hits.map((h) => `${h.type} · ${sevLabel(h.severity)} · ${h.source}`).join("; ")}
                </Callout>
              ) : (
                <Callout tone="ok" icon="checkCircle">
                  Not in your indicator list.
                </Callout>
              )}
              {result.feed?.matched ? (
                <Callout tone="critical" icon="globe" title={`Listed by ${result.feed.ioc?.source || "a public feed"}`}>
                  {[result.feed.ioc?.malware, result.feed.ioc?.description].filter(Boolean).join(" · ") || "Known malicious."}
                </Callout>
              ) : result.feed?.reason === "feeds_unavailable" ? (
                <Callout tone="medium" icon="info">
                  Public feeds are not loaded on this server.
                </Callout>
              ) : (
                <Callout tone="ok" icon="globe">
                  Not listed in the loaded public feeds.
                </Callout>
              )}
            </div>
          )}
        </Card>

        <Card title="Add an indicator" subtitle="Known-bad values to watch for" icon="plus">
          <form onSubmit={add} className="col" style={{ gap: 12 }}>
            <div className="form-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
              <Field label="Type">
                <select className="select" value={form.ioc_type} onChange={(e) => setForm({ ...form, ioc_type: e.target.value })}>
                  {IOC_TYPES.map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Severity">
                <select className="select" value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })}>
                  {SEVERITIES.slice(0, 4).map((s) => (
                    <option key={s} value={s}>
                      {sevLabel(s)}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <Field label="Value" hint={form.ioc_type === "ip" && form.value && !isIPv4(form.value) ? "This doesn't look like an IPv4 address" : undefined}>
              <input className="input input-mono" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} required spellCheck={false} />
            </Field>
            <Field label="Description (optional)">
              <input className="input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} maxLength={200} />
            </Field>
            <Button type="submit" variant="primary" icon="plus" loading={adding} disabled={!form.value.trim()}>
              Add indicator
            </Button>
          </form>
        </Card>
      </div>

      <Card className="span-8" flush title="Your indicators" subtitle={items ? plural(items.length, "active indicator") : "Loading…"} icon="fingerprint" style={{ alignSelf: "start" }}>
        {iocs.error ? (
          <ErrorState detail={iocs.error} onRetry={iocs.reload} />
        ) : !items ? (
          <div className="card-body">
            <SkeletonRows rows={5} />
          </div>
        ) : !items.length ? (
          <Empty icon="fingerprint" title="No indicators yet">
            Add IPs, domains or hashes you already know are malicious. Checks against them are counted as hits.
          </Empty>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Value</th>
                  <th>Type</th>
                  <th>Severity</th>
                  <th className="num">Hits</th>
                  <th className="table-hide-sm">Last seen</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <tr key={i.id}>
                    <td className="mono" style={{ fontSize: 12.5, maxWidth: 280, wordBreak: "break-all" }}>
                      {i.value}
                    </td>
                    <td className="muted">{(IOC_TYPES.find(([v]) => v === i.type) || [0, i.type])[1]}</td>
                    <td>
                      <SevBadge sev={i.severity} />
                    </td>
                    <td className="num">{num(i.hit_count)}</td>
                    <td className="table-hide-sm muted nowrap">{i.last_seen ? fmtDateTime(i.last_seen) : "—"}</td>
                    <td className="num">
                      <button type="button" className="icon-btn icon-btn-sm" aria-label={`Deactivate indicator ${i.value}`} title="Deactivate" onClick={() => setDel(i)}>
                        <Icon name="trash" size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal open={!!del} onClose={() => setDel(null)} label="Deactivate indicator">
        <div className="modal-head">
          <h2>Deactivate this indicator?</h2>
          <p className="mono" style={{ wordBreak: "break-all" }}>
            {del?.value}
          </p>
        </div>
        <div className="modal-body small dim">It stops matching new checks. The record is kept and can be restored from the database.</div>
        <div className="modal-foot">
          <Button variant="ghost" onClick={() => setDel(null)}>
            Cancel
          </Button>
          <Button variant="danger" icon="trash" onClick={remove} data-autofocus>
            Deactivate
          </Button>
        </div>
      </Modal>
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────
export default function Investigations() {
  const c = useConsole();
  const tab = activeTab("investigations", c.tab);
  const inv = usePoll("/api/investigations", { interval: 30000 });
  const [open, setOpen] = useState(null);
  const [results, setResults] = useState(null);

  const onStatus = (id, s) => {
    setResults((r) => (r ? { ...r, items: r.items.map((e) => (e.id === id ? { ...e, status: s } : e)) } : r));
  };

  const counts = useMemo(
    () => ({ campaigns: inv.data?.total_campaigns, anomalies: inv.data?.anomalies?.length }),
    [inv.data],
  );

  return (
    <>
      <PageHeader
        eyebrow="Monitor"
        icon="search"
        title="Investigations"
        description="Connect events, hunt threats, and build a complete investigation."
        actions={
          <Button icon="refresh" variant="ghost" onClick={inv.reload}>
            Refresh
          </Button>
        }
      />
      <PageTabs page="investigations" counts={counts} />
      {tab === "campaigns" && <Campaigns data={inv.data} error={inv.error} reload={inv.reload} />}
      {tab === "hunt" && <Hunt onOpen={setOpen} results={results} setResults={setResults} />}
      {tab === "anomalies" && <Anomalies data={inv.data} error={inv.error} reload={inv.reload} onOpen={setOpen} />}
      {tab === "indicators" && <Indicators />}
      <CaseDrawer event={open} onClose={() => setOpen(null)} onStatus={onStatus} />
    </>
  );
}
